/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { ButtonItem } from '@atlaskit/menu';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const SKILLS_FILTER_CLICKED_INTERACTION = 'skills-filter-menu-item-clicked';

// We want to align styles with the navigation system link menu item
// https://atlassian.design/components/navigation-system/side-nav-items/examples#link-menu-item
const styles = cssMap({
	item: {
		borderRadius: token('radius.small'),
		paddingLeft: token('space.075'),
		fontWeight: token('font.weight.medium'),

		'&::before': {
			content: '""',
			width: '2px',
			height: '12px',
			position: 'absolute',
			insetBlockStart: '50%',
			insetInlineStart: token('space.0'),
			transform: 'translateY(-50%)',
		},

		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> span': {
			gap: token('space.050'),
		},
	},
	itemSelected: {
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	iconPadding: {
		paddingLeft: token('space.050'),
	},
});

type SideNavMenuItemProps = {
	iconBefore?: ReactNode;
	isDisabled?: boolean;
	isSelected: boolean;
	onClick: () => void;
	testId?: string;
	title: ReactNode;
	role?: string;
};

export const SideNavMenuItem = ({
	title,
	isSelected,
	onClick,
	iconBefore,
	testId,
	isDisabled = false,
	role,
}: SideNavMenuItemProps): JSX.Element => {
	return (
		<ButtonItem
			role={role}
			isSelected={isSelected}
			onClick={onClick}
			iconBefore={iconBefore}
			css={[styles.item, isSelected && styles.itemSelected, iconBefore && styles.iconPadding]}
			testId={testId}
			isDisabled={isDisabled}
			interactionName={SKILLS_FILTER_CLICKED_INTERACTION}
		>
			<Text weight="medium" color={isSelected ? 'color.text.selected' : 'color.text.subtle'}>
				{title}
			</Text>
		</ButtonItem>
	);
};
